import React, { useState, useEffect } from "react";
import Template from "../../components/Template";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import AlertDialog from "../../components/AlertDialog";
import FormLabel from "../../components/FormLabel";
import config from "../../config";
import { maskCpfCnpj, maskCurrency } from "../../utils/masks";
import { parseCurrencyToFloat } from "../../utils/utils";

const ImportarNotaFiscalServico = () => {
    const navigate = useNavigate();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [message, setMessage] = useState("");

    const handleCpfCnpjChange = (e) => {
        const value = e.target.value;
        // Aplica a máscara
        const maskedValue = maskCpfCnpj(value);
        setFormData((prev) => ({
            ...prev,
            cnpjEmitente: maskedValue,
        }));
    };

    // Estado do formulário: uploads e campos manuais
    const [formData, setFormData] = useState({
        xml: null,
        pdf: null,
        boleto: null,
        obra: "",
        // Campos manuais para preenchimento (usados se o XML não for enviado)
        valorNota: "",
        descricaoNota: "",
        dataEmissaoManual: "",
        chaveManual: "",
        numeroNota: "",
        cnpjEmitente: "",
        nomeEmitente: "",
        razaoSocialEmitente: "",
        enderecoEmitente: "",
        numeroEndereco: "",
        bairro: "",
        municipio: "",
        uf: "",
        cep: "",
        complemento: "",
        observacao: ""
    });

    // Flag que indica se o usuário optou por preencher manualmente (sem XML)
    const [isManual, setIsManual] = useState(false);

    // Lista de obras para o select (opcional)
    const [obras, setObras] = useState([]);

    useEffect(() => {
        const fetchObras = async () => {
            try {
                const response = await api.get("/obras/listar/todas");
                setObras(response.data);
            } catch (error) {
                console.error("Erro ao carregar obras:", error);
            }
        };
        fetchObras();
    }, []);

    // Função para buscar dados do emitente pelo CNPJ
    const fetchEmitenteData = async (cnpj) => {
        try {
            if (!cnpj) {
                console.warn("CNPJ/CPF está vazio ou nulo.");
                return;
            }

            const cleanCnpj = cnpj.replace(/[^\d]/g, ''); // remove tudo que não for número

            if (cleanCnpj.length === 0) {
                console.warn("CNPJ/CPF contém caracteres inválidos.");
                return;
            }
            const response = await api.get(`/fornecedores/cnpj/${cleanCnpj}`);

            if (response.data) {
                setFormData((prevData) => ({
                    ...prevData,
                    nomeEmitente: response.data.nomeFornecedor || "",
                    razaoSocialEmitente: response.data.razaoSocial || "",
                    enderecoEmitente: response.data.endereco || "",
                    numeroEndereco: response.data.numeroEndereco || "",
                    bairro: response.data.bairro || "",
                    municipio: response.data.municipio || "",
                    uf: response.data.uf || "",
                    cep: response.data.cep || "",
                    complemento: response.data.complemento || "",
                }));
            }
        } catch (error) {
            console.error("Emitente não encontrado.", error);
            // Se não encontrar, deixa os campos vazios para preenchimento manual
            setFormData((prevData) => ({
                ...prevData,
                nomeEmitente: "",
                enderecoEmitente: "",
                razaoSocialEmitente: "",
                numeroEndereco: "",
                bairro: "",
                municipio: "",
                uf: "",
                cep: "",
                complemento: "",
            }));
        }
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: files[0],
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleCnpjBlur = () => {
        if (formData.cnpjEmitente) {
            fetchEmitenteData(formData.cnpjEmitente);
        }
    };

    const handleManualModeToggle = (e) => {
        setIsManual(e.target.checked);
    };

    // Handler para o campo "Valor da Nota" com máscara monetária
    const handleCurrencyChange = (e) => {
        const value = e.target.value;
        const masked = maskCurrency(value);
        setFormData((prevData) => ({
            ...prevData,
            valorNota: masked,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Se não estiver no modo manual, o XML é obrigatório
        if (!isManual && !formData.xml) {
            setMessage("O arquivo XML é obrigatório, ou marque a opção de preenchimento manual.");
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleConfirm = async () => {
        setShowConfirmDialog(false);
        try {
            const data = new FormData();
            if (!isManual && formData.xml) {
                data.append("xml", formData.xml);
            } else {
                // Envia os campos preenchidos manualmente
                data.append("valorNota", parseCurrencyToFloat(formData.valorNota));
                data.append("descricaoNota", formData.descricaoNota);
                data.append("dataEmissao", formData.dataEmissaoManual);
                data.append("chave", formData.chaveManual);
                data.append("numeroNota", formData.numeroNota);
                data.append("cnpjEmitente", formData.cnpjEmitente);
                data.append("nomeEmitente", formData.nomeEmitente);
                data.append("razaoSocialEmitente", formData.razaoSocialEmitente);
                data.append("enderecoEmitente", formData.enderecoEmitente);
                data.append("numeroEndereco", formData.numeroEndereco);
                data.append("bairro", formData.bairro);
                data.append("municipio", formData.municipio);
                data.append("uf", formData.uf);
                data.append("cep", formData.cep);
                data.append("complemento", formData.complemento);
                data.append("observacao", formData.observacao);
            }
            // Os uploads para PDF da nota e PDF do boleto são sempre enviados se selecionados
            if (formData.pdf) {
                data.append("pdf", formData.pdf);
            }
            if (formData.boleto) {
                data.append("boleto", formData.boleto);
            }
            if (formData.obra) {
                data.append("obraId", formData.obra);
            }
            await api.post("/notasfiscaisservicos/import", data, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setShowSuccessDialog(true);
            setTimeout(() => {
                setShowSuccessDialog(false);
                navigate("/notasfiscaisservicos");
            }, 3000);
        } catch (error) {
            console.error("Erro ao importar NFSe:", error);
            setMessage("Erro ao importar a NFSe. Verifique os dados e tente novamente.");
        }
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    const handleBack = () => {
        navigate("/notasfiscaisservicos");
    };

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        Importar Nota Fiscal de Serviço
                    </h1>
                </div>

                {/* Checkbox para modo manual */}
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="manualMode"
                        checked={isManual}
                        onChange={handleManualModeToggle}
                    />
                    <label htmlFor="manualMode" className="text-sm text-stone-600">
                        Não possui XML? Preencher manualmente.
                    </label>
                </div>

                {/* Se não estiver no modo manual, exibe o upload do XML */}
                {!isManual && (
                    <div className="flex flex-col gap-4">
                        <h2 className="text-base text-stone-600 font-medium">Arquivo XML</h2>
                        <div className="flex flex-col gap-1.5">
                            <FormLabel htmlFor="xml" isRequired={true}>
                                Arquivo XML
                            </FormLabel>
                            <div className="relative flex items-center gap-4">
                                <input
                                    id="xml"
                                    name="xml"
                                    type="file"
                                    accept=".xml"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <div className="w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center gap-2">
                                    <span className="text-base text-slate-400">
                                        {formData.xml ? formData.xml.name : "Selecionar arquivo XML"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Upload dos arquivos (PDF da nota e PDF do boleto) - sempre visíveis */}
                <div className="flex flex-col gap-4">
                    <h2 className="text-base text-stone-600 font-medium">Arquivos Adicionais</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* PDF da Nota */}
                        <div className="flex flex-col gap-1.5">
                            <FormLabel htmlFor="pdf" isRequired={false}>
                                Arquivo PDF da Nota (Opcional)
                            </FormLabel>
                            <div className="relative flex items-center gap-4">
                                <input
                                    id="pdf"
                                    name="pdf"
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <div className="w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center gap-2">
                                    <span className="text-base text-slate-400">
                                        {formData.pdf ? formData.pdf.name : "Selecionar arquivo PDF da nota"}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {/* PDF do Boleto */}
                        <div className="flex flex-col gap-1">
                            <FormLabel htmlFor="boleto" isRequired={false}>
                                Arquivo PDF do Boleto (Opcional)
                            </FormLabel>
                            <div className="relative">
                                <input
                                    type="file"
                                    name="boleto"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <div className="w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center">
                                    <span className="text-base text-slate-400">
                                        {formData.boleto ? formData.boleto.name : "Selecionar arquivo PDF do boleto"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Campo Opcional para Obra */}
                <div className="flex flex-col gap-4">
                    <FormLabel htmlFor="obra" isRequired={false}>
                        Obra (Opcional)
                    </FormLabel>
                    <select
                        name="obra"
                        id="obra"
                        value={formData.obra}
                        onChange={handleInputChange}
                        className="px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                    >
                        <option value="">Selecionar</option>
                        {obras.map((obra) => (
                            <option key={obra.id} value={obra.id}>
                                {obra.nomeProjeto.toUpperCase()}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Se estiver no modo manual, exibe os campos manuais para preenchimento dos dados da nota e do emitente */}
                {isManual && (
                    <div className="flex flex-col gap-4">
                        <h2 className="text-base text-stone-600 font-medium">Dados da Nota</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="valorNota" isRequired={true}>
                                    Valor da Nota
                                </FormLabel>
                                <input
                                    type="text"
                                    name="valorNota"
                                    id="valorNota"
                                    value={formData.valorNota}
                                    onChange={handleCurrencyChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite o valor da nota"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="descricaoNota" isRequired={true}>
                                    Descrição da Nota
                                </FormLabel>
                                <input
                                    type="text"
                                    name="descricaoNota"
                                    id="descricaoNota"
                                    value={formData.descricaoNota}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite a descrição da nota"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="dataEmissaoManual" isRequired={true}>
                                    Data de Emissão
                                </FormLabel>
                                <input
                                    type="date"
                                    name="dataEmissaoManual"
                                    id="dataEmissaoManual"
                                    value={formData.dataEmissaoManual}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="chaveManual" isRequired={true}>
                                    Chave da Nota
                                </FormLabel>
                                <input
                                    type="text"
                                    name="chaveManual"
                                    id="chaveManual"
                                    value={formData.chaveManual}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite a chave da nota"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="numeroNota" >
                                    Número da Nota
                                </FormLabel>
                                <input
                                    type="number"
                                    name="numeroNota"
                                    id="numeroNota"
                                    value={formData.numeroNota}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite o número da nota"
                                />
                            </div>
                        </div>
                        <h2 className="text-base text-stone-600 font-medium mt-4">Dados do Emitente</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="cnpjEmitente" isRequired={true}>
                                    CNPJ do Emitente
                                </FormLabel>
                                <input
                                    type="text"
                                    name="cnpjEmitente"
                                    id="cnpjEmitente"
                                    value={formData.cnpjEmitente}
                                    onChange={handleCpfCnpjChange}
                                    onBlur={handleCnpjBlur}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite o CNPJ do emitente"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="nomeEmitente" isRequired={true}>
                                    Nome do Emitente
                                </FormLabel>
                                <input
                                    type="text"
                                    name="nomeEmitente"
                                    id="nomeEmitente"
                                    value={formData.nomeEmitente}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Nome do emitente"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="nomeEmitente" isRequired={false}>
                                    Razão Social do Emitente
                                </FormLabel>
                                <input
                                    type="text"
                                    name="razaoSocialEmitente"
                                    id="razaoSocialEmitente"
                                    value={formData.razaoSocialEmitente}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Razão Social do emitente"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="enderecoEmitente" isRequired={false}>
                                    Endereço do Emitente
                                </FormLabel>
                                <input
                                    type="text"
                                    name="enderecoEmitente"
                                    id="enderecoEmitente"
                                    value={formData.enderecoEmitente}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Endereço completo"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="numeroEndereco" isRequired={false}>
                                    Número
                                </FormLabel>
                                <input
                                    type="text"
                                    name="numeroEndereco"
                                    id="numeroEndereco"
                                    value={formData.numeroEndereco}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Número"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="bairro" isRequired={false}>
                                    Bairro
                                </FormLabel>
                                <input
                                    type="text"
                                    name="bairro"
                                    id="bairro"
                                    value={formData.bairro}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Bairro"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="municipio" isRequired={false}>
                                    Município
                                </FormLabel>
                                <input
                                    type="text"
                                    name="municipio"
                                    id="municipio"
                                    value={formData.municipio}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Município"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="uf" isRequired={false}>
                                    UF
                                </FormLabel>
                                <input
                                    type="text"
                                    name="uf"
                                    id="uf"
                                    value={formData.uf}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="UF"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="cep" isRequired={false}>
                                    CEP
                                </FormLabel>
                                <input
                                    type="text"
                                    name="cep"
                                    id="cep"
                                    value={formData.cep}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="CEP"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="complemento" isRequired={false}>
                                    Complemento
                                </FormLabel>
                                <input
                                    type="text"
                                    name="complemento"
                                    id="complemento"
                                    value={formData.complemento}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Complemento"
                                />
                            </div>
                        </div>

                        {/* Observações */}
                        <div className="flex flex-col gap-4">
                            <h2 className="text-base text-stone-600 font-medium">Observações</h2>
                            <textarea
                                name="observacao"
                                rows={4}
                                value={formData.observacao}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-sm text-slate-700 resize-none"
                                placeholder="Digite suas observações aqui..."
                            />
                        </div>
                    </div>
                )}

                {/* Mensagem de Alerta */}
                {message && (
                    <AlertDialog
                        isVisible={true}
                        message={message}
                        onClose={() => setMessage("")}
                    />
                )}

                {/* Botões de Ação */}
                <div className="flex items-center w-full gap-4">
                    <button
                        type="button"
                        className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                        onClick={handleBack}
                    >
                        <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                        <span>Voltar</span>
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                    >
                        <img className="w-4 h-4" src="/images/icon/save.png" alt="Importar" />
                        <span>Importar</span>
                    </button>
                </div>

                {/* Modais */}
                <ConfirmationModal
                    isVisible={showConfirmDialog}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    message="Deseja importar esta NFSe?"
                    textButtonConfirm="Importar"
                />
                <SuccessModal
                    isVisible={showSuccessDialog}
                    message="NFSe importada com sucesso!"
                    onClose={() => setShowSuccessDialog(false)}
                />
            </form>
        </Template>
    );
};

export default ImportarNotaFiscalServico;
